import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn, Card, Badge, SectionTitle } from "../primitives";

export const SlaTargetsManager = () => {
  const [targets, setTargets] = useState<any[]>([]);
  const [bUnits, setBUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<Record<string, any>>({ agreement_type: '', business_unit_id: '', target_days: 7, severity: 'standard', is_active: true });

  const load = async () => {
    setLoading(true);
    const [t, b] = await Promise.all([
      apiCall('/settings/sla-targets'),
      apiCall('/settings/business-units'),
    ]);
    if (t?.slaTargets) setTargets(t.slaTargets);
    if (b?.businessUnits) setBUnits(b.businessUnits);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.target_days || Number(form.target_days) <= 0) return alert('target_days must be > 0');
    const r = await apiCall('/settings/sla-targets', {
      method: 'POST',
      body: JSON.stringify({
        agreement_type: form.agreement_type || null,
        business_unit_id: form.business_unit_id || null,
        target_days: Number(form.target_days),
        severity: form.severity,
        is_active: form.is_active,
      }),
    });
    if (r?.slaTarget) { setForm({ agreement_type: '', business_unit_id: '', target_days: 7, severity: 'standard', is_active: true }); load(); }
    else alert(r?.error || 'Failed');
  };

  const toggleActive = async (t: any) => {
    await apiCall(`/settings/sla-targets/${t.id}`, { method: 'PUT', body: JSON.stringify({ is_active: !t.is_active }) });
    load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this SLA target?')) return;
    await apiCall(`/settings/sla-targets/${id}`, { method: 'DELETE' });
    load();
  };

  const severityColor = (s: string) => s === 'critical' ? T.red : s === 'high' ? T.ylw : T.acc;

  if (loading) return <div style={{ padding: 20, color: T.txD }}>Loading SLA targets...</div>;

  const inputStyle = { padding: '6px 8px', borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' as const, width: '100%' };

  return (
    <div>
      <SectionTitle sub="SLA deadlines per (agreement type, business unit) combination. More-specific targets win. Blank = matches any.">SLA Targets</SectionTitle>

      <Card style={{ marginBottom: 12, padding: 12, backgroundColor: T.bg2 }}>
        <div style={{ fontSize: 11, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700, marginBottom: 8 }}>New SLA Target</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr) auto', gap: 8, alignItems: 'end' }}>
          <div><div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Agreement Type</div><input value={form.agreement_type} onChange={e => setForm((p: any) => ({ ...p, agreement_type: e.target.value }))} placeholder="(any)" style={inputStyle} /></div>
          <div><div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Business Unit</div><select value={form.business_unit_id} onChange={e => setForm((p: any) => ({ ...p, business_unit_id: e.target.value }))} style={inputStyle as any}><option value="">(any)</option>{bUnits.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
          <div><div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Target Days *</div><input type="number" min={1} value={form.target_days} onChange={e => setForm((p: any) => ({ ...p, target_days: e.target.value }))} style={inputStyle} /></div>
          <div><div style={{ fontSize: 10, color: T.txD, marginBottom: 2 }}>Severity</div><select value={form.severity} onChange={e => setForm((p: any) => ({ ...p, severity: e.target.value }))} style={inputStyle as any}><option value="standard">standard</option><option value="high">high</option><option value="critical">critical</option></select></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.tx }}><input type="checkbox" checked={form.is_active} onChange={e => setForm((p: any) => ({ ...p, is_active: e.target.checked }))} /> Active</label>
          <Btn v="primary" sz="sm" onClick={() => { add(); }}>Add Target</Btn>
        </div>
      </Card>

      <Card style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${T.brd}` }}>
              {["Agreement Type", "Business Unit", "Target Days", "Severity", "Active", ""].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, color: T.txD, textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {targets.map((t: any) => (
              <tr key={t.id} style={{ borderBottom: `1px solid ${T.brd}` }}>
                <td style={{ padding: '8px 10px', color: T.tx }}>{t.agreement_type || <span style={{ color: T.txD }}>(any)</span>}</td>
                <td style={{ padding: '8px 10px', color: T.tx }}>{t.business_unit_name || <span style={{ color: T.txD }}>(any)</span>}</td>
                <td style={{ padding: '8px 10px', color: T.tx, fontVariantNumeric: 'tabular-nums' }}>{t.target_days}</td>
                <td style={{ padding: '8px 10px' }}><Badge c={severityColor(t.severity)} b={T.bg3}>{t.severity}</Badge></td>
                <td style={{ padding: '8px 10px' }}><button onClick={() => toggleActive(t)} style={{ fontSize: 10, color: t.is_active ? T.grn : T.txD, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>{t.is_active ? 'Active' : 'Inactive'}</button></td>
                <td style={{ padding: '8px 10px' }}><button onClick={() => remove(t.id)} style={{ fontSize: 10, color: T.red, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Delete</button></td>
              </tr>
            ))}
            {targets.length === 0 && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: T.txD, fontSize: 12 }}>No SLA targets yet. Add one above.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
